import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "formgrid grid" }
const _hoisted_3 = { class: "field col-12" }
const _hoisted_4 = { class: "flex flex-wrap align-items-center mb-0 gap-1" }
const _hoisted_5 = { for: "email" }
const _hoisted_6 = {
  key: 2,
  class: "p-error"
}
const _hoisted_7 = { class: "field col-12" }
const _hoisted_8 = {
  key: 0,
  class: "field col-12"
}

import { computed, onMounted, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import InputText from 'primevue/inputtext';
import Skeleton from 'primevue/skeleton';
import { useToast } from 'primevue/usetoast';
import { PersonStores, usePeopleTypesStore } from '@/store';
import { can, usePersonTypes } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { ExternalAssociationInterface } from '@/types';
import InfoTooltip from '@/components/InfoTooltip';


export default /*@__PURE__*/_defineComponent({
  __name: 'ExternalAssociations',
  setup(__props) {

const profileStore = PersonStores.useUserProfileStore();
const toast = useToast();
const externalAssociation = ref({} as ExternalAssociationInterface);
const $externalResults = ref({});
const ssisColabUserId = ref<string>('');
const originalSsisColabUserId = ref<string>('');
const elumaStudentId = ref<string>('');
const PEOPLE_TYPES = usePeopleTypesStore().PEOPLE_TYPES;
const errors = ref(null);

const rules = computed(() => {
  return {
    external_id: {
      required,
      minLengthValue: minLength(2),
    },
  };
});

const ssisRules = {
  ssisColabUserId: {},
};

const v$Ssis = useVuelidate(ssisRules, { ssisColabUserId });

const isStudent = computed(() => usePersonTypes.isStudent(profileStore.person?.people_type?.acronym));

const v$ = useVuelidate(rules, externalAssociation, { $externalResults });

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => {
  setExternalAssociation();
});

/**
 * Set the original externalAssociation
 */
const setExternalAssociation = () => {
  if (profileStore.person) {
    externalAssociation.value = clonedExternalAssociation();
    ssisColabUserId.value = profileStore.person.ssis_colab_user_id || '';
    originalSsisColabUserId.value = profileStore.person.ssis_colab_user_id || '';
    elumaStudentId.value = profileStore.person.eluma_student_id || '';
  }
};

/**
 * Return a cloned version of the external association
 */
const clonedExternalAssociation = () => {
  return { ...profileStore.person.external_association };
};

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = externalAssociation.value;

  if (isStudent.value) {
    return {
      external_id: value.external_id,
      organization_id: !value.organization_id ? profileStore.person?.organizations?.[0] : value.organization_id,
    };
  }

  return {
    external_id: value.external_id,
    organization_id: value.organization_id,
  };
});

watch(() => profileStore.person, setExternalAssociation);

/**
 * Check if SSIS CoLab ID has changed
 */
const hasSsisColabChanged = () => ssisColabUserId.value !== originalSsisColabUserId.value;

/**
 * Check if External ID has changed
 */
const hasExternalIdChanged = () => v$.value.$anyDirty && externalAssociation.value.external_id;

/**
 * Update SSIS CoLab ID only
 */
const updateSsisColabOnly = async () => {
  const ssisPayload = {
    ssis_colab_user_id: ssisColabUserId.value || null,
  };

  try {
    await profileStore.updatePersonField(ssisPayload);
    originalSsisColabUserId.value = ssisColabUserId.value;
    profileStore.person.ssis_colab_user_id = ssisColabUserId.value;
    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'SSIS CoLab ID has been updated',
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update SSIS CoLab ID',
      life: 3000,
    });
  }
};

/**
 * Validate external ID update for students
 */
const validateStudentExternalId = () => {
  if (isStudent.value && !payload.value.organization_id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'The Student must be connected to an Organization prior to assigning an SSID',
      life: 3000,
    });
    return false;
  }
  return true;
};

/**
 * Update External ID with optional SSIS CoLab
 */
const updateExternalIdWithOptionalSsis = async (includeSSIS: boolean) => {
  if (!(await v$.value.$validate())) return;

  if (!validateStudentExternalId()) return;

  const updatePayload = { ...payload.value };
  if (includeSSIS) {
    updatePayload.ssis_colab_user_id = ssisColabUserId.value || null;
  }

  try {
    const result = await profileStore.updateExternalId(updatePayload);
    handleResult(result);
    doSuccess();
    if (includeSSIS) {
      originalSsisColabUserId.value = ssisColabUserId.value;
      profileStore.person.ssis_colab_user_id = ssisColabUserId.value;
    }
  } catch (response) {
    doFail(response);
  }
};

/**
 * on save external_id, reset dirty and push to API
 */
const onSave = async () => {
  if (!profileStore.person) return;

  const ssisChanged = hasSsisColabChanged();
  const externalIdChanged = hasExternalIdChanged();

  // Handle SSIS CoLab update only
  if (ssisChanged && !externalIdChanged) {
    await updateSsisColabOnly();
    return;
  }

  // Handle External ID update (with or without SSIS CoLab)
  if (externalIdChanged) {
    await updateExternalIdWithOptionalSsis(ssisChanged);
  }
  resetDirty();
};

/**
 * reset the form dirty settting
 * which hides the footer buttons and style
 */
const resetDirty = () => {
  v$.value.$reset();
  v$.value.$clearExternalResults();
};

/**
 * on cancel reset the dirty and reset externalID
 */
const onCancel = () => {
  setExternalAssociation();
  resetDirty();
  v$Ssis.value.$reset();
};

/**
 * Display the toast message.
 */
const doToast = (error = false) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: error ? 'there was an error' : 'External Association has been saved',
    life: 3000,
  });
};

const doSuccess = () => {
  if (!profileStore.person.external_association) {
    profileStore.person.external_association = {} as ExternalAssociationInterface;
  }
  profileStore.person.external_association.external_id = externalAssociation.value.external_id;
  onCancel();
};

const doFail = (response) => {
  if (response.data.errors.external_id) {
    $externalResults.value = {
      external_id: response.data.errors.external_id,
    };
  }
};

/**
 * handle the update results
 */
const handleResult = (result: number | undefined) => {
  if (result === 200) {
    doToast();
  } else {
    doToast(true);
  }
};

const isDirty = computed(() => {
  return v$.value.$anyDirty || ssisColabUserId.value !== originalSsisColabUserId.value;
});

const canUpdateExternalAssociations = () => {
  const personType: string = profileStore.person?.people_type?.acronym as string;
  let permissionString = 'PEOPLE_UPDATE_EXTERNAL_ASSOCIATIONS';
  let str: keyof typeof PERMISSIONS;
  switch (personType) {
    case PEOPLE_TYPES.SUPPORT_ADMIN: {
      permissionString += '_SUPPORT_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SCHOOL_ADMIN: {
      permissionString += '_SCHOOL_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SUPER_ADMIN: {
      permissionString += '_SUPER_ADMIN';
      break;
    }
  }
  str = permissionString as keyof typeof PERMISSIONS;

  return can([PERMISSIONS[str]]);
};

return (_ctx: any,_cache: any) => {
  const _component_FormCard = _resolveComponent("FormCard")!

  return (_openBlock(), _createBlock(_component_FormCard, {
    title: "External Associations",
    dirty: isDirty.value,
    cancel: onCancel,
    save: onSave,
    "save-disabled": !canUpdateExternalAssociations() || (_unref(v$).$invalid && _unref(v$).$dirty)
  }, {
    form: _withCtx(() => [
      _createElementVNode("form", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("label", _hoisted_5, _toDisplayString(isStudent.value ? 'SSID' : 'External ID'), 1),
              (isStudent.value)
                ? (_openBlock(), _createBlock(_unref(InfoTooltip), {
                    key: 0,
                    class: "cursor-pointer"
                  }, {
                    default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                      _createElementVNode("span", null, " Enter SSID (Statewide Student Identifier). If that is unknown please use District/School ID) ", -1)
                    ]))]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            (_unref(profileStore).loading)
              ? (_openBlock(), _createBlock(_unref(Skeleton), {
                  key: 0,
                  width: "100%",
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_unref(InputText), {
                  key: 1,
                  id: "external_id",
                  modelValue: _unref(v$).external_id.$model,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).external_id.$model) = $event)),
                  class: _normalizeClass({ 'p-invalid': _unref(v$).$invalid && _unref(v$).$dirty }),
                  disabled: !canUpdateExternalAssociations(),
                  onInput: _unref(v$).$touch
                }, null, 8, ["modelValue", "class", "disabled", "onInput"])),
            (errors.value && errors.value.external_id)
              ? (_openBlock(), _createElementBlock("small", _hoisted_6, _toDisplayString(errors.value.external_id[0]), 1))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).external_id.$externalResults, (error) => {
              return (_openBlock(), _createElementBlock("small", {
                key: error.$uid,
                class: "p-error"
              }, _toDisplayString(error.$message), 1))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_7, [
            _cache[4] || (_cache[4] = _createElementVNode("label", { for: "ssis_colab" }, "SSIS CoLab", -1)),
            (_unref(profileStore).loading)
              ? (_openBlock(), _createBlock(_unref(Skeleton), {
                  key: 0,
                  width: "100%",
                  height: "2.3rem"
                }))
              : (_openBlock(), _createBlock(_unref(InputText), {
                  key: 1,
                  id: "ssis_colab",
                  modelValue: ssisColabUserId.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((ssisColabUserId).value = $event)),
                  placeholder: "Enter alphanumeric ID",
                  disabled: !canUpdateExternalAssociations(),
                  onInput: _unref(v$Ssis).$touch
                }, null, 8, ["modelValue", "disabled", "onInput"]))
          ]),
          (isStudent.value && _unref(can)([_unref(PERMISSIONS).STUDENT_VIEW_ELUMA_STUDENT_ID]))
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _cache[5] || (_cache[5] = _createElementVNode("label", { for: "eluma_student_id" }, "Eluma Student ID", -1)),
                (_unref(profileStore).loading)
                  ? (_openBlock(), _createBlock(_unref(Skeleton), {
                      key: 0,
                      width: "100%",
                      height: "2.3rem"
                    }))
                  : (_openBlock(), _createBlock(_unref(InputText), {
                      key: 1,
                      id: "eluma_student_id",
                      modelValue: elumaStudentId.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((elumaStudentId).value = $event)),
                      placeholder: "Eluma Student ID",
                      disabled: true
                    }, null, 8, ["modelValue"]))
              ]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["dirty", "save-disabled"]))
}
}

})